import { nextTick } from "vue";
import manuscriptData from "../../data/manuscripts.json";
import incunabulaData from "../../data/incunables.json";
import tocData from "../../data/toc.json";

type Facsimile = {
    id?: string;
    href: string;
    iiifMetadataLink?: string | null;
};

type Variant = {
    id: string;
    htmlFile: string;
    url: string;
    facsimiles?: Facsimile[];
    isTranslation?: boolean;
};

type Work = {
    workId: string;
    variants: Variant[];
};

type CollationEntry = {
    start: string;
    end: string;
    textN?: string | null;
} | null;

const typedTocData = tocData as Work[];
const typedManuscriptData = manuscriptData as Array<{
    id: string;
    licence?: unknown;
}>;
const typedIncunabulaData = incunabulaData as Array<{
    id: string;
    licence?: unknown;
}>;

const baseUrlRaw: string = import.meta.env.BASE_URL;
const baseUrl = baseUrlRaw.endsWith("/") ? baseUrlRaw : baseUrlRaw + "/";
const basename = (p: string): string => String(p).split("/").pop() ?? "";

// IIIF REST API syntax: {scheme}://{server}{/prefix}/{identifier}/{region}/{size}/{rotation}/{quality}.{format}
const iiifRegEx =
    /^(https?:\/\/)(.+?\/)(full|(pct:)?[\d.]+,[\d.]+,[\d.]+,[\d.]+)\/(full|!?(pct:)?[\d.,]+)\/!?[\d.]+\/(color|gray|bitonal|default)[./](jpg|tif|png|gif|jp2|pdf|webp)$/;

function getWork(workId: string): Work | undefined {
    return typedTocData.find((item) => item.workId === workId);
}

function getVariant(workId: string, variantId: string): Variant | undefined {
    return getWork(workId)?.variants.find((v) => v.id === variantId);
}

function getFacsimilesForVariant(
    workId: string,
    variantId: string,
): Facsimile[] {
    const facsimiles = getVariant(workId, variantId)?.facsimiles;

    if (!facsimiles) {
        return [];
    }

    return facsimiles.map((f) => {
        const iiifMatches = f.href.match(iiifRegEx);

        if (iiifMatches) {
            return {
                ...f,
                iiifMetadataLink: "https://" + iiifMatches[2],
            };
        } else if (!f.href.startsWith("http")) {
            const localIiifUrl =
                (import.meta.env.DEV ? "https://rke.pagina-dh.de" : "") +
                "/iiif/" +
                basename(f.href).replace(
                    /\.([Jj][Pp][Ee]?[Gg]|[Tt][Ii][Ff]+)/,
                    ".ptif",
                ) +
                "/info.json";

            return { ...f, iiifMetadataLink: localIiifUrl };
        } else {
            return { ...f, iiifMetadataLink: null };
        }
    });
}

function normalizeRelativeUrl(p: string | null | undefined): string {
    return String(p || "")
        .trim()
        .replace(/^\/+/, "")
        .replace(/^\.\//, "");
}

function looksLikeAppShell(html: string): boolean {
    const hasAppRoot = html.includes('<div id="app"');
    const hasViteClient = html.includes("@vite/client");
    const hasBuiltAssets =
        html.includes("/assets/") && html.includes('type="module"');

    return hasAppRoot && (hasViteClient || hasBuiltAssets);
}

function isFullHtmlDocument(html: string): boolean {
    return (
        /<!doctype html/i.test(html) &&
        /<html\b/i.test(html) &&
        /<head\b/i.test(html)
    );
}

function looksLikeTranscriptHtml(html: string): boolean {
    return (
        html.includes('class="body') ||
        html.includes('class="l"') ||
        html.includes('class="pb"') ||
        html.includes('class="cb"') ||
        html.includes('id="pb_') ||
        html.includes('id="cb_') ||
        html.includes('data-type="line-content"')
    );
}

function pathToAbsolute(rel: string): string {
    const cleaned = normalizeRelativeUrl(rel);
    const base = new URL(baseUrl, window.location.origin);

    return new URL(cleaned, base).href;
}

async function getHtmlForUrl(
    relativeUrl: string,
    fallbackUrl: string | null = null,
): Promise<string> {
    const absoluteUrl = pathToAbsolute(relativeUrl);
    const fallbackAbsoluteUrl = fallbackUrl
        ? pathToAbsolute(fallbackUrl)
        : null;

    let response: Response;
    let text: string;

    try {
        response = await fetch(absoluteUrl, { cache: "no-store" });
        text = await response.text();
    } catch (e) {
        console.error("[HTML] fetch failed", absoluteUrl, e);
        throw e;
    }

    const isShell = looksLikeAppShell(text);
    const isDoc = isFullHtmlDocument(text);
    const isTranscript = looksLikeTranscriptHtml(text);

    if (
        (response.status >= 400 || isShell || isDoc || !isTranscript) &&
        fallbackAbsoluteUrl
    ) {
        const fallbackResponse = await fetch(fallbackAbsoluteUrl, {
            cache: "no-store",
        });
        return await fallbackResponse.text();
    }

    if (isShell) {
        console.error("[HTML] got app shell instead of text HTML!", {
            absoluteUrl,
        });
    }

    return text;
}

function getHtmlForVariant(
    workId: string,
    variantId: string,
    useProse = false,
    useInterlinear = false,
): Promise<string> {
    const variant = getVariant(workId, variantId);

    if (!variant) {
        return Promise.reject(
            new Error(
                `Variant not found: workId=${workId}, variantId=${variantId}`,
            ),
        );
    }

    let relativeUrl = variant.htmlFile;

    if (useProse) {
        relativeUrl = relativeUrl.replace(/\/text\//, "/text/prose/");
    } else if (useInterlinear) {
        const interlinearUrl = relativeUrl.replace(
            /\/text\//,
            "/text/interlinear/",
        );
        return getHtmlForUrl(interlinearUrl, relativeUrl);
    }

    return getHtmlForUrl(relativeUrl);
}

/* function currently not used; can be removed, if not put into use anymore */
async function getJsonForVariant(
    workId: string,
    variantId: string,
): Promise<unknown> {
    const variant = getVariant(workId, variantId);

    if (!variant) {
        throw new Error(
            `Variant not found: workId=${workId}, variantId=${variantId}`,
        );
    }

    const filename = variant.url.split("/").pop();

    if (!filename) {
        throw new Error(`Invalid variant URL: ${variant.url}`);
    }

    const variantUrl =
        baseUrl + "data/text/" + filename.replace(/\.[Xx][Mm][Ll]$/, ".json");

    const response = await fetch(variantUrl);
    return await response.json();
}

function getManuscriptLicence(manuscriptId: string): unknown | null {
    return (
        typedManuscriptData.find((item) => item.id === manuscriptId)?.licence ??
        typedIncunabulaData.find((item) => item.id === manuscriptId)?.licence ??
        null
    );
}

/* function currently not used; can be removed, if not put into use anymore */
async function getTextForVariant(
    workId: string,
    variantId: string,
): Promise<string[]> {
    const variant = getVariant(workId, variantId);

    if (!variant) {
        throw new Error(
            `Variant not found: workId=${workId}, variantId=${variantId}`,
        );
    }

    const variantUrl = baseUrl + variant.url;
    const response = await fetch(variantUrl);
    const xmlText = await response.text();

    const teiDoc = new DOMParser().parseFromString(xmlText, "text/xml");

    const lines = teiSelect(
        "/tei:TEI/tei:text//tei:body/tei:l",
        teiDoc,
    ) as Element[];

    return lines.map(
        (line, i) =>
            "<p>" +
            ((i + 1) % 5 === 0
                ? '<span class="is-size-7 has-background-dark has-text-white transcript-line-number">' +
                  (i + 1) +
                  "</span> "
                : "") +
            line.toString() +
            "</p>",
    );
}

function getVariantsForWork(workId: string): Variant[] {
    return getWork(workId)?.variants.filter((v) => !v.isTranslation) ?? [];
}

function getInterlinearSwitch(line: Element): HTMLElement | null {
    return line.querySelector(".interlinear-switch");
}

// functions for handling interlinear display of variants

function closeAllInterlinear(editionHtml: ParentNode | null): void {
    if (editionHtml) {
        const mainLines = editionHtml.querySelectorAll("div.has-interlinear");

        mainLines.forEach((line) => {
            closeInterlinear(line, editionHtml);
        });
    }
}

function closeInterlinear(line: Element, editionHtml: ParentNode): void {
    const sw = getInterlinearSwitch(line);

    if (sw) sw.setAttribute("aria-expanded", "false");

    const position = line.getAttribute("n");

    editionHtml
        .querySelectorAll(`div.interlinear[data-leader-line="${position}"]`)
        .forEach((vLine) => vLine.classList.add("hidden"));
}

function initInterLinearSelectors(editionHtml: ParentNode | null): void {
    if (!editionHtml) return;

    nextTick(() => {
        const mainLines = editionHtml.querySelectorAll("div.has-interlinear");

        mainLines.forEach((line) => {
            const sw = getInterlinearSwitch(line);

            if (!sw) return;

            if (sw.dataset.interlinearInit === "true") return;
            sw.dataset.interlinearInit = "true";

            sw.setAttribute("role", "button");
            sw.setAttribute("tabindex", "0");
            sw.setAttribute("aria-expanded", "false");

            closeInterlinear(line, editionHtml);

            sw.addEventListener("click", () =>
                toggleInterlinear(line, editionHtml),
            );

            sw.addEventListener("keydown", (event: KeyboardEvent) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    toggleInterlinear(line, editionHtml);
                }
            });
        });
    });
}

function openAllInterlinear(editionHtml: ParentNode | null): void {
    if (editionHtml) {
        const mainLines = editionHtml.querySelectorAll("div.has-interlinear");

        mainLines.forEach((line) => {
            openInterlinear(line, editionHtml);
        });
    }
}

function openInterlinear(line: Element, editionHtml: ParentNode): void {
    const sw = getInterlinearSwitch(line);

    if (sw) sw.setAttribute("aria-expanded", "true");

    const position = line.getAttribute("n");

    editionHtml
        .querySelectorAll(`div.interlinear[data-leader-line="${position}"]`)
        .forEach((vLine) => vLine.classList.remove("hidden"));
}

function toggleInterlinear(line: Element, editionHtml: ParentNode): void {
    const sw = getInterlinearSwitch(line);
    const expanded = sw?.getAttribute("aria-expanded") === "true";

    if (expanded) closeInterlinear(line, editionHtml);
    else openInterlinear(line, editionHtml);
}

function translateAlignmentPoint(
    alignmentPoint: string | number,
    alignmentPointTextN: string | null | undefined,
    sourceCollationTable: CollationEntry[],
    targetCollationTable: CollationEntry[],
): number | null {
    let sourcePos: number | null = null;
    const parsedAlignmentPoint = parseInt(String(alignmentPoint), 10);

    for (let i = 0; i < sourceCollationTable.length; i++) {
        const sourceEntry = sourceCollationTable[i];

        if (!sourceEntry) continue;

        if (
            parseInt(sourceEntry.start, 10) <= parsedAlignmentPoint &&
            parseInt(sourceEntry.end, 10) >= parsedAlignmentPoint &&
            (!alignmentPointTextN || alignmentPointTextN === sourceEntry.textN)
        ) {
            sourcePos = i;
            break;
        }
    }

    if (sourcePos === null) return null;

    const sourceEntry = sourceCollationTable[sourcePos];
    const targetEntry = targetCollationTable[sourcePos];

    if (
        !sourceEntry ||
        !targetEntry ||
        (sourceEntry.textN && sourceEntry.textN !== targetEntry.textN)
    ) {
        return null;
    }

    return (
        parsedAlignmentPoint +
        parseInt(targetEntry.start, 10) -
        parseInt(sourceEntry.start, 10)
    );
}

export {
    closeAllInterlinear,
    getFacsimilesForVariant,
    getHtmlForUrl,
    getHtmlForVariant,
    getJsonForVariant,
    getManuscriptLicence,
    getTextForVariant,
    getVariantsForWork,
    initInterLinearSelectors,
    openAllInterlinear,
    translateAlignmentPoint,
};
